import { usePage, Link, router } from '@inertiajs/react'
import { useState } from 'react'
import type { ReactNode } from 'react'
import { ErrorBoundary } from '@/components/ui/ErrorBoundary'
import { ToastContainer } from '@/components/ui/Toast'
import type { SharedProps } from '@/types/inertia'
import { WeatherIcon } from '@/lib/icons'

export function AppLayout({ children }: { children: ReactNode }) {
  const { auth, flash, weather_navbar: weather } = usePage<SharedProps>().props
  const [menuOpen, setMenuOpen] = useState(false)

  const handleLogout = () => {
    router.delete('/session')
  }

  const navLinks = [
    { href: '/', label: 'Spis treści' },
    { href: '/plants', label: 'Rośliny' },
    { href: '/plant_species', label: 'Baza gatunków' },
    { href: '/locations', label: 'Miejsca' },
    { href: '/supplies', label: 'Zapasy' },
    { href: '/calendar', label: 'Kalendarz' },
  ]

  return (
    <div className="min-h-screen bg-desk font-body text-ink">
      {/* Navbar */}
      <nav className="bg-spine text-paper shadow-page">
        <div className="mx-auto flex max-w-[72rem] items-center justify-between px-md py-sm">
          <div className="flex items-center gap-md">
            <Link href="/" className="font-display text-lg tracking-widest uppercase text-paper">
              Grządka
            </Link>
            {weather && (
              <Link
                href="/weather"
                className="hidden md:inline font-label text-sm text-paper/80 transition-colors hover:text-paper"
                title={`${weather.weather_description}, wilg. ${weather.humidity}%`}
              >
                <WeatherIcon code={weather.weather_code} isDay={weather.is_day} size={18} className="text-paper/80 inline-block align-text-bottom" /> {Math.round(weather.temperature)}°C · {weather.city_name}
              </Link>
            )}
          </div>

          {/* Desktop nav */}
          <div className="hidden items-center gap-md md:flex">
            {auth.user && navLinks.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                className="font-label text-sm tracking-wide text-paper/80 transition-colors hover:text-paper"
              >
                {link.label}
              </Link>
            ))}
            {auth.user ? (
              <>
                <Link
                  href="/settings"
                  className="font-label text-sm tracking-wide text-paper/60 transition-colors hover:text-paper"
                >
                  Ustawienia
                </Link>
                <button
                  onClick={handleLogout}
                  className="font-label text-sm tracking-wide text-paper/60 transition-colors hover:text-paper"
                >
                  Wyloguj
                </button>
              </>
            ) : (
              <Link
                href="/sign_in"
                className="font-label text-sm tracking-wide text-paper/80 transition-colors hover:text-paper"
              >
                Zaloguj
              </Link>
            )}
          </div>

          {/* Mobile hamburger */}
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="flex flex-col gap-[4px] md:hidden"
            aria-label="Menu"
          >
            <span className={`block h-[2px] w-5 bg-paper transition-transform ${menuOpen ? 'translate-y-[6px] rotate-45' : ''}`} />
            <span className={`block h-[2px] w-5 bg-paper transition-opacity ${menuOpen ? 'opacity-0' : ''}`} />
            <span className={`block h-[2px] w-5 bg-paper transition-transform ${menuOpen ? '-translate-y-[6px] -rotate-45' : ''}`} />
          </button>
        </div>

        {/* Mobile menu */}
        {menuOpen && (
          <div className="border-t border-paper/20 px-md pb-md md:hidden">
            {weather && (
              <div className="py-xs font-label text-sm text-paper/70">
                <WeatherIcon code={weather.weather_code} isDay={weather.is_day} size={14} className="text-paper/70 inline-block align-text-bottom" />
                {' '}{Math.round(weather.temperature)}°C &middot; {weather.weather_description}
              </div>
            )}
            {auth.user && navLinks.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                onClick={() => setMenuOpen(false)}
                className="block py-xs font-label text-sm text-paper/80"
              >
                {link.label}
              </Link>
            ))}
            {auth.user ? (
              <>
                <Link
                  href="/settings"
                  onClick={() => setMenuOpen(false)}
                  className="block py-xs font-label text-sm text-paper/60"
                >
                  Ustawienia
                </Link>
                <button
                  onClick={handleLogout}
                  className="block py-xs font-label text-sm text-paper/60"
                >
                  Wyloguj
                </button>
              </>
            ) : (
              <Link
                href="/sign_in"
                onClick={() => setMenuOpen(false)}
                className="block py-xs font-label text-sm text-paper/80"
              >
                Zaloguj
              </Link>
            )}
          </div>
        )}
      </nav>

      {/* Toast notifications */}
      <ToastContainer notice={flash.notice} alert={flash.alert} />

      {/* Main content */}
      <main className="pb-2xl">
        <ErrorBoundary>{children}</ErrorBoundary>
      </main>

    </div>
  )
}
